import React, { useState, useEffect, useRef } from 'react';
import { candidateAPI } from '../services/api';
import toast from 'react-hot-toast';
import JobApplicationModal from './JobApplicationModal';
import { downloadResumeAuthenticated } from '../utils/resumeUrl';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to normalize job IDs for comparison
const normalizeJobId = (id) => {
  if (!id) return '';
  // Convert to string, trim, and convert to lowercase for UUID comparison
  return String(id).trim().toLowerCase();
};

// Helper function to get proxied URL for job files from S3
const getProxiedFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  
  // If it's an S3 URL, proxy it through the backend to avoid CORS issues
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    // Check if it's an S3 URL
    if (fileUrl.includes('s3.amazonaws.com') || (fileUrl.includes('s3.') && fileUrl.includes('.amazonaws.com'))) {
      try {
        const url = new URL(fileUrl);
        // Extract the path (S3 key) - remove leading slash
        let s3Key = url.pathname.substring(1);
        // Remove query parameters
        s3Key = s3Key.split('?')[0];
        
        if (s3Key) {
          // Use proxy pattern for job files
          return `${API_BASE_URL}/jobs/files/${s3Key}`;
        }
      } catch (e) {
        console.error('Error parsing file URL:', e);
      }
      // Fallback: return original URL
      return fileUrl;
    }
    // Not an S3 URL, return as-is
    return fileUrl;
  }
  // Local path, prepend API base URL
  return `${API_BASE_URL}${fileUrl}`;
};

// Normalize list-like API fields that may arrive as array, JSON string, comma/newline text, or null.
const normalizeListField = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch (_) {
      // Fall through to split parsing.
    }
    return trimmed
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
};

const JobListings = () => {
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAllJobs, setLoadingAllJobs] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showApplicationDetailsModal, setShowApplicationDetailsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [skillsUsed, setSkillsUsed] = useState([]);
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());
  const [applications, setApplications] = useState([]);
  const [applicationsLoaded, setApplicationsLoaded] = useState(false);
  const pageSize = 10;
  const allJobsFetchedRef = useRef(false);

  // Fetch recommended jobs when page changes
  useEffect(() => {
    fetchJobs();
  }, [page]);

  // Fetch all jobs and applications only once on mount
  useEffect(() => {
    console.log('🔄 useEffect for fetchAllJobs and fetchMyApplications triggered');
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        console.log('📞 Calling fetchAllJobs...');
        await fetchAllJobs();
        console.log('📞 Calling fetchMyApplications...');
        await fetchMyApplications();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Debug: Log when appliedJobIds changes
  useEffect(() => {
    console.log('Applied job IDs updated:', Array.from(appliedJobIds));
  }, [appliedJobIds]);

  // Re-sync applied status when both jobs and applications are loaded
  useEffect(() => {
    if (jobs.length > 0 && applications.length > 0 && applicationsLoaded) {
      const newAppliedIds = new Set();
      applications.forEach(app => {
        const jobId = app.jobId || app.job_id || app.job?.id || '';
        if (jobId) {
          newAppliedIds.add(normalizeJobId(jobId));
        }
      });
      
      // Check if we need to update
      const currentIds = Array.from(appliedJobIds);
      const newIds = Array.from(newAppliedIds);
      if (JSON.stringify(currentIds.sort()) !== JSON.stringify(newIds.sort())) {
        console.log('🔄 Re-syncing applied job IDs:', {
          old: currentIds,
          new: newIds
        });
        setAppliedJobIds(newAppliedIds);
      }
    }
  }, [jobs, applications, applicationsLoaded]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      // Fetch jobs with skill matching (recommended jobs)
      const response = await candidateAPI.getJobRecommendations(page, pageSize);
      const jobsList = response.data.jobs || [];
      
      // Filter to only include jobs that have skill matches
      // Since this is the recommendations endpoint with skills, all returned jobs should be skill-matched
      // Only exclude jobs that explicitly have matchScore = 0 AND no matchedSkills
      const skillMatchedJobs = jobsList.filter(matchedJob => {
        const matchScore = matchedJob.matchScore;
        const matchedSkills = matchedJob.matchedSkills || [];
        
        // Exclude only if matchScore is explicitly 0 AND there are no matchedSkills
        // Otherwise, include the job (it's from recommendations endpoint, so it should be matched)
        if (matchScore === 0 && (!matchedSkills || matchedSkills.length === 0)) {
          return false; // Explicitly no match
        }
        
        // Include all other jobs (they have matches or matchScore is undefined/null)
        return true;
      });
      
      console.log('📋 Recommended Jobs filtering:', {
        totalJobs: jobsList.length,
        skillMatchedJobs: skillMatchedJobs.length,
        sampleJob: jobsList[0] ? {
          matchScore: jobsList[0].matchScore,
          matchedSkills: jobsList[0].matchedSkills
        } : null
      });
      
      setJobs(skillMatchedJobs);
      setTotal(response.data.total || 0);
      setSkillsUsed(response.data.skillsUsed || []);
      
      // Debug: Log job IDs from recommendations
      const jobIdsFromRecommendations = skillMatchedJobs.map(j => {
        const job = j.job || j;
        return normalizeJobId(job.id || job.jobId || '');
      }).filter(id => id);
      console.log('📋 Recommended Jobs (skill-matched):', jobIdsFromRecommendations.length, 'jobs');
    } catch (error) {
      console.error('Error fetching job recommendations:', error);
      toast.error('Failed to load job recommendations');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllJobs = async () => {
    // Skip if already fetched
    if (allJobsFetchedRef.current) {
      return;
    }
    
    // Skip if already loading
    if (loadingAllJobs) {
      return;
    }
    
    try {
      allJobsFetchedRef.current = true;
      setLoadingAllJobs(true);
      
      // Fetch all jobs without skill filter to get jobs that don't match skills
      const response = await candidateAPI.getJobRecommendations(1, 50, null);
      const jobsList = response.data.jobs || [];
      
      // Filter to only include jobs with NO skill matches
      const nonMatchedJobs = jobsList.filter(matchedJob => {
        const matchScore = matchedJob.matchScore;
        const matchedSkills = matchedJob.matchedSkills || [];
        
        // Include jobs with matchScore = 0 or undefined AND no matchedSkills
        const hasNoMatch = (matchScore === undefined || matchScore === null || matchScore === 0);
        const hasNoMatchedSkills = !matchedSkills || matchedSkills.length === 0;
        
        return hasNoMatch && hasNoMatchedSkills;
      });
      
      console.log('📋 Non-matched jobs fetched:', {
        totalJobs: jobsList.length,
        nonMatchedJobs: nonMatchedJobs.length
      });
      
      // Store non-matched jobs
      setAllJobs(nonMatchedJobs);
    } catch (error) {
      console.error('Error fetching all jobs:', error);
      setAllJobs([]);
      allJobsFetchedRef.current = false; // Reset on error so it can retry
    } finally {
      setLoadingAllJobs(false);
    }
  };

  const fetchMyApplications = async () => {
    try {
      setApplicationsLoaded(false);
      const response = await candidateAPI.getMyApplications();
      const apps = response.data.applications || response.data || [];
      setApplications(apps);
      
      // Create a set of applied job IDs - ensure all IDs are normalized for consistent comparison
      const appliedIds = new Set();
      apps.forEach((app, index) => {
        // Try multiple possible field names
        const jobId = app.jobId || app.job_id || app.job?.id || '';
        if (jobId) {
          const normalizedId = normalizeJobId(jobId);
          if (normalizedId) {
            appliedIds.add(normalizedId);
          }
        }
        
        // Debug: Log first application structure
        if (index === 0) {
          console.log('📝 First application full structure:', JSON.stringify(app, null, 2));
        }
      });
      
      setAppliedJobIds(appliedIds);
      setApplicationsLoaded(true);
      console.log('✅ Fetched applications:', apps.length);
      console.log('✅ Applied job IDs (normalized):', Array.from(appliedIds));
      console.log('✅ Applied job IDs (raw values):', apps.map(app => ({
        jobId: app.jobId,
        job_id: app.job_id,
        jobIdType: typeof app.jobId,
        job_idType: typeof app.job_id
      })));
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
      setApplicationsLoaded(true); // Mark as loaded even on error to prevent infinite loading
      // Don't show error toast - applications might not be available yet
    }
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setShowApplicationModal(true);
  };

  const handleApplicationSuccess = async (jobId) => {
    setShowApplicationModal(false);
    
    // Extract the actual job object to get the correct ID
    const job = selectedJob;
    if (!job) {
      console.error('❌ No selected job found when handling application success');
      return;
    }
    
    // Get job ID from multiple possible locations (same logic as in render)
    const actualJobId = job.id || job.jobId || (job.job && job.job.id) || (job.job && job.job.jobId) || jobId;
    const normalizedId = normalizeJobId(actualJobId);
    
    console.log('🎯 Application success - Job ID details:', {
      passedJobId: jobId,
      extractedJobId: actualJobId,
      normalizedId: normalizedId,
      jobObject: job
    });
    
    // Optimistically update the applied status immediately
    if (normalizedId) {
      setAppliedJobIds(prev => {
        const newSet = new Set(prev);
        newSet.add(normalizedId);
        console.log('✅ Optimistically added job ID to applied set:', normalizedId);
        console.log('✅ Updated applied job IDs:', Array.from(newSet));
        return newSet;
      });
      
      // Also add a temporary application object to the applications array
      setApplications(prev => {
        const tempApp = {
          id: 'temp-' + Date.now(),
          jobId: actualJobId,
          job_id: actualJobId,
          status: 'submitted',
          createdAt: new Date().toISOString()
        };
        // Check if application already exists
        const exists = prev.some(app => {
          const appJobId = normalizeJobId(app.jobId || app.job_id || '');
          return appJobId === normalizedId;
        });
        if (!exists) {
          console.log('✅ Added temporary application object:', tempApp);
          return [...prev, tempApp];
        }
        return prev;
      });
    }
    
    setSelectedJob(null);
    
    // Refresh applications list to get the actual application data
    await fetchMyApplications();
    
    // Refresh jobs list (but don't wait, let it happen in background)
    fetchJobs();
    
    toast.success('Application submitted successfully!');
  };

  const getApplicationForJob = (jobId) => {
    if (!jobId) return null;
    const normalizedJobId = normalizeJobId(jobId);
    return applications.find(app => {
      const appJobId = app.jobId || app.job_id || app.job?.id || '';
      const normalizedAppJobId = normalizeJobId(appJobId);
      return normalizedAppJobId === normalizedJobId && normalizedAppJobId !== '';
    });
  };

  const handleViewApplication = (job) => {
    const application = getApplicationForJob(job.id);
    if (application) {
      setSelectedApplication(application);
      setSelectedJob(job);
      setShowApplicationDetailsModal(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 4) return 'bg-blue-100 text-blue-800';
    if (score >= 2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Helper function to render a compact job card
  const renderJobCard = (matchedJob, showMatchScore = true) => {
    const job = matchedJob.job || matchedJob;
    const matchScore = matchedJob.matchScore || 0;
    const matchedSkills = matchedJob.matchedSkills || [];
    
    // Extract job ID
    const jobId = job.id || job.jobId || matchedJob.jobId || matchedJob.id || 
                (job.job && job.job.id) || (job.job && job.job.jobId);
    const jobIdStr = normalizeJobId(jobId);
    
    // Check if applied
    let isApplied = jobIdStr && appliedJobIds.has(jobIdStr);
    if (!isApplied && jobIdStr && applications.length > 0) {
      const foundApp = applications.find(app => {
        const appJobId = normalizeJobId(app.jobId || app.job_id || app.job?.id || '');
        return appJobId === jobIdStr && appJobId !== '';
      });
      isApplied = !!foundApp;
    }

    return (
      <div
        key={job.id}
        className="ui-card-interactive p-4 flex flex-col h-full"
      >
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-base font-bold text-gray-900 flex-1 line-clamp-2" title={job.title}>
              {job.title}
            </h3>
            {showMatchScore && matchScore > 0 && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ml-2 ${getMatchScoreColor(
                  matchScore
                )}`}
              >
                {matchScore}%
              </span>
            )}
          </div>

          {/* Company Name */}
          {job.companyName && (
            <p className="text-sm text-gray-600 mb-2">{job.companyName}</p>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mb-2">
            {job.location && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="truncate">{job.location}</span>
              </div>
            )}
            {job.jobType && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h-8m8 0v2a2 2 0 01-2 2H8a2 2 0 01-2-2V6" />
                </svg>
                <span>{job.jobType}</span>
              </div>
            )}
          </div>

          {/* Required Skills - Show first 3 */}
          {normalizeListField(job.requiredSkills).length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-1">
                {normalizeListField(job.requiredSkills).slice(0, 3).map((skill, index) => {
                  const isMatched = matchedSkills.includes(skill);
                  return (
                    <span
                      key={index}
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        isMatched
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {skill}
                    </span>
                  );
                })}
                {normalizeListField(job.requiredSkills).length > 3 && (
                  <span className="px-1.5 py-0.5 text-xs text-gray-500">
                    +{normalizeListField(job.requiredSkills).length - 3}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-auto pt-2 border-t border-gray-100 space-y-2">
          <button
            onClick={() => {
              setSelectedJobForDetails(job);
              setShowJobDetailsModal(true);
            }}
            className="w-full px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium flex items-center justify-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Details
          </button>
          {isApplied ? (
            <button
              onClick={() => handleViewApplication(job)}
              className="w-full px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium"
            >
              View Applied
            </button>
          ) : (
            <button
              onClick={() => handleApply(job)}
              className="w-full px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-xs font-medium"
            >
              Apply Now
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const selectedRequirements = normalizeListField(selectedJobForDetails?.requirements);
  const selectedResponsibilities = normalizeListField(selectedJobForDetails?.responsibilities);
  const selectedRequiredSkills = normalizeListField(selectedJobForDetails?.requiredSkills);
  const selectedBenefits = normalizeListField(selectedJobForDetails?.benefits);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Recommended Jobs</h2>
        <p className="text-sm text-gray-600 mt-1">Jobs matched to your skills and experience</p>
      </div>

      {/* Skills Used Info */}
      {skillsUsed.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
          <p className="text-xs text-primary-800 mb-1.5">
            <strong>Matching based on your skills:</strong>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {skillsUsed.map((skill, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Jobs - Grid Layout */}
      {jobs.length === 0 ? (
        <div className="ui-card empty-state">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No jobs found</h3>
          <p className="mt-2 text-sm text-gray-500">
            We couldn't find any job recommendations matching your skills at the moment.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {jobs.map((matchedJob) => renderJobCard(matchedJob, true))}
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} jobs
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page * pageSize >= total}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* You May Also Be Interested In - Jobs with NO skill matches */}
      {(() => {
        // Get IDs of recommended jobs to exclude duplicates
        const recommendedJobIds = new Set();
        jobs.forEach(matchedJob => {
          const job = matchedJob.job || matchedJob;
          const jobId = job.id || job.jobId || matchedJob.jobId || matchedJob.id;
          if (jobId) {
            recommendedJobIds.add(normalizeJobId(jobId));
          }
        });
        
        // Filter out jobs that are already in recommended jobs
        // allJobs already contains only non-matched jobs, but we need to exclude duplicates
        const nonMatchedJobs = allJobs.filter(matchedJob => {
          const job = matchedJob.job || matchedJob;
          const jobId = job.id || job.jobId || matchedJob.jobId || matchedJob.id;
          const normalizedId = normalizeJobId(jobId);
          
          // Exclude if already in recommended jobs
          return !recommendedJobIds.has(normalizedId);
        });
        
        console.log('🔍 You May Also Be Interested In:');
        console.log('  Recommended job IDs:', Array.from(recommendedJobIds));
        console.log('  Non-matched jobs (from API):', allJobs.length);
        console.log('  Final non-matched jobs (after dedupe):', nonMatchedJobs.length);
        
        return nonMatchedJobs.length > 0 ? (
          <div className="mt-8">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900">You May Also Be Interested In</h2>
              <p className="text-sm text-gray-600 mt-1">Jobs that may not match your current skills but could be of interest</p>
            </div>
            
            {loadingAllJobs ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                {nonMatchedJobs.map((matchedJob) => renderJobCard(matchedJob, false))}
              </div>
            )}
          </div>
        ) : null;
      })()}

      {/* Job Details Modal */}
      {showJobDetailsModal && selectedJobForDetails && (
        <div className="modal-backdrop flex items-center justify-center p-4">
          <div className="modal-panel max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Job Details</h2>
              <button
                onClick={() => {
                  setShowJobDetailsModal(false);
                  setSelectedJobForDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedJobForDetails.title}</h3>
                {selectedJobForDetails.companyName && (
                  <p className="text-lg text-gray-600 mb-3">{selectedJobForDetails.companyName}</p>
                )}
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                  {selectedJobForDetails.location && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{selectedJobForDetails.location}</span>
                    </div>
                  )}
                  {selectedJobForDetails.jobType && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h-8m8 0v2a2 2 0 01-2 2H8a2 2 0 01-2-2V6" />
                      </svg>
                      <span>{selectedJobForDetails.jobType}</span>
                    </div>
                  )}
                  {selectedJobForDetails.applicationDeadline && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Apply by {formatDate(selectedJobForDetails.applicationDeadline)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Job Description */}
              {selectedJobForDetails.description && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Job Description</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedJobForDetails.description}</div>
                </div>
              )}

              {/* Requirements */}
              {selectedRequirements.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Requirements</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {selectedRequirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Responsibilities */}
              {selectedResponsibilities.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Responsibilities</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {selectedResponsibilities.map((resp, index) => (
                      <li key={index}>{resp}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Required Skills */}
              {selectedRequiredSkills.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Required Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequiredSkills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits */}
              {selectedBenefits.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Benefits</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {selectedBenefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Salary Range */}
              {(selectedJobForDetails.salaryRange || selectedJobForDetails.salary_range) && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Salary Range</h4>
                  <p className="text-sm text-gray-700">
                    {selectedJobForDetails.salaryRange || selectedJobForDetails.salary_range}
                    <span className="text-gray-500 text-xs ml-1">(per annum)</span>
                  </p>
                </div>
              )}

              {/* JD File */}
              {(() => {
                let jdFileUrl = null;
                if (selectedJobForDetails.tags) {
                  if (typeof selectedJobForDetails.tags === 'object' && !Array.isArray(selectedJobForDetails.tags)) {
                    jdFileUrl = selectedJobForDetails.tags.upload_file_url || selectedJobForDetails.tags.uploadFileUrl;
                  } else if (Array.isArray(selectedJobForDetails.tags)) {
                    const tagObj = selectedJobForDetails.tags.find(t => typeof t === 'object' && (t.upload_file_url || t.uploadFileUrl));
                    if (tagObj) {
                      jdFileUrl = tagObj.upload_file_url || tagObj.uploadFileUrl;
                    }
                  }
                }
                return jdFileUrl ? (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Job Description File</h4>
                    <a
                      href={getProxiedFileUrl(jdFileUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download JD
                    </a>
                  </div>
                ) : null;
              })()}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {(() => {
                  const jobId = selectedJobForDetails.id || selectedJobForDetails.jobId;
                  const normalizedId = normalizeJobId(jobId);
                  const isJobApplied = normalizedId && appliedJobIds.has(normalizedId);
                  
                  if (isJobApplied) {
                    return (
                      <button
                        onClick={() => {
                          const application = getApplicationForJob(normalizedId);
                          if (application) {
                            setSelectedApplication(application);
                            setSelectedJob(selectedJobForDetails);
                            setShowJobDetailsModal(false);
                            setShowApplicationDetailsModal(true);
                          }
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        View Application
                      </button>
                    );
                  } else {
                    return (
                      <button
                        onClick={() => {
                          setSelectedJob(selectedJobForDetails);
                          setShowJobDetailsModal(false);
                          setShowApplicationModal(true);
                        }}
                        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Apply Now
                      </button>
                    );
                  }
                })()}
                <button
                  onClick={() => {
                    setShowJobDetailsModal(false);
                    setSelectedJobForDetails(null);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application Modal */}
      {showApplicationModal && selectedJob && (
        <JobApplicationModal
          job={selectedJob}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedJob(null);
          }}
          onSuccess={handleApplicationSuccess}
        />
      )}

      {/* Application Details Modal */}
      {showApplicationDetailsModal && selectedApplication && selectedJob && (
        <div className="modal-backdrop flex items-center justify-center p-4">
          <div className="modal-panel max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Application Details</h2>
              <button
                onClick={() => {
                  setShowApplicationDetailsModal(false);
                  setSelectedApplication(null);
                  setSelectedJob(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Job Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Job Applied For</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-sm font-medium text-gray-700 min-w-[80px]">Title:</span>
                    <span className="text-sm text-gray-900">{selectedJob.title}</span>
                  </div>
                  {selectedJob.location && (
                    <div className="flex items-start">
                      <span className="text-sm font-medium text-gray-700 min-w-[80px]">Location:</span>
                      <span className="text-sm text-gray-900">{selectedJob.location}</span>
                    </div>
                  )}
                  {selectedJob.companyName && (
                    <div className="flex items-start">
                      <span className="text-sm font-medium text-gray-700 min-w-[80px]">Company:</span>
                      <span className="text-sm text-gray-900">{selectedJob.companyName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Application Status */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Application Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedApplication.status?.toLowerCase() === 'accepted' ? 'bg-green-100 text-green-800' :
                      selectedApplication.status?.toLowerCase() === 'rejected' ? 'bg-red-100 text-red-800' :
                      selectedApplication.status?.toLowerCase() === 'reviewed' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {(selectedApplication.status || 'Submitted').charAt(0).toUpperCase() + (selectedApplication.status || 'Submitted').slice(1).toLowerCase()}
                    </span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-sm font-medium text-gray-700 min-w-[100px]">Applied On:</span>
                    <span className="text-sm text-gray-900">
                      {formatDate(
                        selectedApplication.appliedAt || 
                        selectedApplication.applied_at || 
                        selectedApplication.createdAt || 
                        selectedApplication.created_at || 
                        selectedApplication.submittedAt || 
                        selectedApplication.submitted_at
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cover Letter */}
              {(selectedApplication.coverLetter || selectedApplication.cover_letter) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Cover Letter</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedApplication.coverLetter || selectedApplication.cover_letter}
                    </p>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {(selectedApplication.expectedSalary || selectedApplication.expected_salary || 
                selectedApplication.noticePeriod || selectedApplication.notice_period ||
                selectedApplication.availabilityDate || selectedApplication.availability_date ||
                selectedApplication.referralSource || selectedApplication.referral_source) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Additional Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {(selectedApplication.expectedSalary || selectedApplication.expected_salary) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Expected Salary:</span>
                        <span className="text-sm text-gray-900">
                          {selectedApplication.expectedSalary || selectedApplication.expected_salary}
                          {(selectedApplication.expectedSalary || selectedApplication.expected_salary) && (
                            <span className="text-gray-500 text-xs ml-1">(per annum)</span>
                          )}
                        </span>
                      </div>
                    )}
                    {(selectedApplication.noticePeriod || selectedApplication.notice_period) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Notice Period:</span>
                        <span className="text-sm text-gray-900">
                          {selectedApplication.noticePeriod || selectedApplication.notice_period}
                        </span>
                      </div>
                    )}
                    {(selectedApplication.availabilityDate || selectedApplication.availability_date) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Available From:</span>
                        <span className="text-sm text-gray-900">
                          {formatDate(selectedApplication.availabilityDate || selectedApplication.availability_date)}
                        </span>
                      </div>
                    )}
                    {(selectedApplication.referralSource || selectedApplication.referral_source) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Referral Source:</span>
                        <span className="text-sm text-gray-900">
                          {selectedApplication.referralSource || selectedApplication.referral_source}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents & Links */}
              {(selectedApplication.resumeUrl || selectedApplication.resume_url || 
                selectedApplication.portfolioUrl || selectedApplication.portfolio_url) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Documents & Links</h3>
                  <div className="flex flex-wrap gap-3">
                    {(selectedApplication.resumeUrl || selectedApplication.resume_url) && (
                      <button
                        type="button"
                        onClick={() =>
                          downloadResumeAuthenticated(
                            selectedApplication.resumeUrl || selectedApplication.resume_url
                          ).catch(() => toast.error('Could not download resume'))
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        View Resume
                      </button>
                    )}
                    {(selectedApplication.portfolioUrl || selectedApplication.portfolio_url) && (
                      <a
                        href={selectedApplication.portfolioUrl || selectedApplication.portfolio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                        View Portfolio
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobListings;

